import * as Sentry from '@sentry/react';
import {
  FieldTextAreaCompact,
  FieldTextCompact,
  Modal,
  ModalActions,
  ModalContent,
  Pushbutton
} from '@stratumn/atomic';
import to from 'await-to-js';
import graphql from 'babel-plugin-relay/macro';
import TypeFileViewsMenu from 'components/ui/typeFileViewsMenu';
import compose from 'lodash.flowright';
import { addBot } from 'mutations';
import PropTypes from 'prop-types';
import { useState } from 'react';
import ReactDOM from 'react-dom';
import injectSheet from 'react-jss';
import { createFragmentContainer } from 'react-relay';
import { SpanType, withSpanAsync } from 'tracing';
import { downloadCsv } from 'utils/downloadCsv';
import { downloadXls } from 'utils/downloadXls';
import { withGqlClient, withUser } from 'wrappers';

import styles from './botsHeader.style';

const INITIAL_STATE = {
  name: '',
  key: '',
  description: '',
  submitted: false,
  showModal: false
};

export const BotsHeader = props => {
  const [state, setState] = useState(INITIAL_STATE);
  const [isExportOptionShown, setIsExportOptionShown] = useState(false);

  const toggleModal = () =>
    state.showModal
      ? setState(prevState => ({
          ...prevState,
          showModal: false,
          name: ''
        }))
      : setState(prevState => ({
          ...prevState,
          showModal: true
        }));

  const handleChange = name => e =>
    setState(prevState => ({
      ...prevState,
      [name]: e.target.value
    }));

  const resetState = () => setState(INITIAL_STATE);

  const submitNewBot = () =>
    withSpanAsync('addBot', SpanType.processing, async () => {
      const {
        environment,
        organization: { id, rowId }
      } = props;

      const { name, key, description } = state;

      setState(prevState => ({
        ...prevState,
        submitted: true
      }));

      /*
        In order for the mutation to create the new Bot, we need to leave
        ** empty strings ** for the avatar field.
      */
      const [err] = await to(
        addBot(environment, id, rowId, name, key, description, '')
      );

      if (err) {
        Sentry.captureException(err);
        setState(prevState => ({
          ...prevState,
          submitted: false
        }));
      } else {
        resetState();
      }

      return {};
    });

  const handleDownload = fileType => {
    const { headerBots } = props.organization;
    const sortedBots = [...headerBots.edges].sort((a, b) =>
      a.node.name.localeCompare(b.node.name)
    );

    const headers = ['Bot Name', 'Team Name'];
    const rows = sortedBots.flatMap(bot => {
      const teamNames = bot.node.teams.nodes.map(team => team.name);
      if (teamNames.length === 0) {
        return [
          [
            bot.node.name,
            '' // Empty string for Team Name when there are no teams
          ]
        ];
      }
      return teamNames.map(teamName => [bot.node.name, teamName]);
    });

    if (fileType === 'csv') {
      downloadCsv(headers, rows, 'members.csv');
    } else if (fileType === 'excel') {
      downloadXls(headers, rows, 'members.xls');
    }
  };

  const toggleShowExportOption = () => {
    setIsExportOptionShown(prev => !prev);
  };

  const {
    classes,
    organization: {
      account: { canUpdate }
    }
  } = props;
  const { name, key, description, submitted, showModal } = state;

  return ReactDOM.createPortal(
    <>
      <div className={classes.buttonContainer}>
        <Pushbutton
          data-testid="export-data-button"
          onClick={toggleShowExportOption}
          secondary
        >
          Export data
        </Pushbutton>
        {canUpdate && (
          <Pushbutton
            data-testid="create-new-bot-button"
            onClick={toggleModal}
            primary
          >
            Create New Bot
          </Pushbutton>
        )}
        {isExportOptionShown && (
          <TypeFileViewsMenu
            onClose={toggleShowExportOption}
            onDownload={handleDownload}
          />
        )}
      </div>
      {showModal && (
        <Modal
          closeButtonLabel="Cancel"
          data-testid="create-new-bot-modal"
          handleCollapse={toggleModal}
          title="Create new Bot"
        >
          <ModalContent>
            <FieldTextCompact
              data-testid="bot-name-input"
              label="Bot name"
              onValueChange={handleChange('name')}
              value={name}
            />
            <div className={classes.modalSecondaryField}>
              <FieldTextAreaCompact
                data-testid="bot-key-input"
                label="Bot public signing Key"
                noResize
                onValueChange={handleChange('key')}
                rows={4}
                value={key}
              />
            </div>
            <div className={classes.modalSecondaryField}>
              <FieldTextAreaCompact
                data-testid="bot-description-input"
                label="Bot description"
                noResize
                onValueChange={handleChange('description')}
                rows={4}
                value={description}
              />
            </div>
          </ModalContent>
          <ModalActions>
            <Pushbutton data-testid="cancel-button" onClick={toggleModal}>
              cancel
            </Pushbutton>
            <Pushbutton
              data-testid="create-bot-button"
              disabled={!(name && key) || submitted}
              onClick={submitNewBot}
              primary
            >
              create bot
            </Pushbutton>
          </ModalActions>
        </Modal>
      )}
    </>,
    document.getElementById('btn-create-new-bot')
  );
};

BotsHeader.propTypes = {
  classes: PropTypes.object.isRequired,
  organization: PropTypes.object.isRequired
};

export default createFragmentContainer(
  compose(withUser, withGqlClient, injectSheet(styles))(BotsHeader),
  {
    organization: graphql`
      fragment botsHeader_organization on Organization {
        id # in order to update the store
        rowId
        account {
          canUpdate
        }
        headerBots: bots {
          edges {
            node {
              id
              rowId
              name
              teams {
                totalCount
                nodes {
                  name
                }
              }
            }
          }
        }
      }
    `
  }
);
